<template>
  <div id="ChangePasswordForm">
    <div class="modal-card fixed-width dtac-font">
      <!-- Modal Title -->
      <header class="modal-card-head">
        <p v-if="isEditMode" class="modal-card-title">UPDATE USER INFO.</p>
        <p v-else class="modal-card-title">CREATE NEW USER</p>
      </header>
      <!-- Modal Description + Input -->
      <section class="modal-card-body">
        <div class="columns is-multiline">
          <div class="column is-12">
            <b-field label="Full Name">
              <b-input required v-model="fullname" />
            </b-field>
          </div>
          <div class="column is-12">
            <b-field label="Email">
              <b-input
                type="email"
                required
                v-model="email"
                :disabled="isEditMode"
              />
            </b-field>
          </div>
          <div class="column" :class="isEditMode ? 'is-12' : 'is-6'">
            <b-field label="Username">
              <b-input required v-model="username" :disabled="isEditMode" />
            </b-field>
          </div>
          <div v-if="!isEditMode" class="column is-6">
            <b-field label="Temporary Password">
              <b-input
                required
                type="password"
                password-reveal
                v-model="password"
              />
            </b-field>
          </div>
          <div class="column is-6">
            <b-field label="Role">
              <b-select
                placeholder="Select a Role"
                v-model="selected_role"
                expanded
              >
                <option v-for="role in role_list" :value="role" :key="role">
                  {{ role }}
                </option>
              </b-select>
            </b-field>
          </div>
          <div class="column is-6">
            <b-field label="Business Unit">
              <b-select
                placeholder="Select a subject"
                v-model="selected_bu"
                expanded
              >
                <option
                  v-for="business_unit in business_unit_list"
                  :value="business_unit"
                  :key="business_unit._id"
                >
                  {{ business_unit.business_unit_name }}
                </option>
              </b-select>
            </b-field>
          </div>
          <div v-if="isShowManagerList" class="column is-12">
            <b-field label="Manager">
              <b-select
                placeholder="Select a Manager"
                v-model="selected_manager"
                expanded
              >
                <option
                  v-for="manager_info in manager_list"
                  :value="manager_info"
                  :key="manager_info.username"
                >
                  {{ manager_info.name }} | {{ manager_info.email }}
                </option>
              </b-select>
            </b-field>
          </div>
          <div class="column is-12">
            <b-field label="Remark (Optional)">
              <b-input maxlength="200" type="textarea" v-model="remark" />
            </b-field>
          </div>
        </div>
      </section>
      <!-- Modal Footer -->
      <footer class="modal-card-foot" style="justify-content: flex-end">
        <!-- Button: Create + Save -->
        <button
          class="button is-primary"
          :disabled="isDisableButtonAction"
          @click.prevent="performButtonAction()"
        >
          {{ isEditMode ? "Update" : "Create" }}
        </button>
      </footer>
    </div>
  </div>
</template>

<script>
import Helpers from "@/utils/Helper";

import UserApi from "@/services/UserApi";
import BusinessUnitApi from "@/services/BusinessUnitApi";

import AlertManager from "@/utils/AlertManager";

export default {
  name: "CreateNewUserForm",
  props: ["mode", "selected_user"],
  data() {
    return {
      username: null,
      password: null,
      fullname: null,
      email: null,
      remark: null,
      selected_role: null,
      role_list: ["admin", "manager", "agent"],
      selected_bu: null,
      business_unit_list: [],
      selected_manager: null
    };
  },
  computed: {
    manager_list: function() {
      if (this.selected_bu) {
        return this.selected_bu.managerList;
      } else {
        return [];
      }
    },
    isEditMode: function() {
      switch (this.mode) {
        case "edit":
          return true;
        default:
          return false;
      }
    },
    isShowManagerList: function() {
      if (this.selected_bu && this.selected_role === "agent") {
        return this.selected_bu.managerList.length > 0;
      } else {
        return false;
      }
    },
    isDisableButtonAction: function() {
      if (
        this.username &&
        this.username !== "" &&
        this.fullname &&
        this.fullname !== "" &&
        this.email &&
        this.email !== "" &&
        this.selected_role &&
        this.selected_bu
      ) {
        if (this.isEditMode) {
          return false;
        } else {
          return !(this.password && this.password !== "");
        }
      } else {
        return true;
      }
    }
  },
  methods: {
    performButtonAction: function() {
      const params = this.createUserPayload();
      if (this.isEditMode) {
        this.requestUpdateUserInfo(params);
      } else {
        this.requestCreateNewUser(params);
      }
    },
    createUserPayload: function() {
      let payload = {
        username: this.username,
        name: this.fullname,
        email: this.email,
        role: this.selected_role,
        business_unit_id: this.selected_bu._id
      };
      if (this.selected_manager) {
        payload.supervisor_id = this.selected_manager._id;
      }
      if (this.remark && this.remark !== "") {
        payload.remark = this.remark;
      }
      switch (this.mode) {
        case "create":
          if (this.password && this.password !== "") {
            payload.password = this.password;
          }
          return payload;

        default:
          return payload;
      }
    },
    mappingBusinessUnitAndManagerById: function(
      business_unit_id,
      supervisor_id
    ) {
      const found_businessunit = Helpers.mappingBusinessUnitById(
        this.business_unit_list,
        business_unit_id
      );
      this.selected_bu = found_businessunit;
      if (this.selected_role === "agent") {
        const found_supervisor = Helpers.mappingManagerById(
          this.selected_bu.managerList,
          supervisor_id
        );
        this.selected_manager = found_supervisor;
      }
    },
    requestCreateNewUser: async function(params) {
      const loadingComponent = this.$buefy.loading.open({
        container: null
      });
      const response = await UserApi.requestCreateNewUser(params);
      loadingComponent.close();

      if (response.status) {
        this.$emit("close");
        this.$emit("create_success");
        AlertManager.showSuccessSnackbar(response.message);
      } else {
        AlertManager.showErrorSnackbar(response.message);
      }
    },
    requestUpdateUserInfo: async function(params) {
      const loadingComponent = this.$buefy.loading.open({
        container: null
      });
      const response = await UserApi.requestUpdateUserInfoById(
        this.selected_user._id,
        params
      );
      loadingComponent.close();

      if (response.status) {
        this.$emit("close");
        this.$emit("create_success");
        AlertManager.showSuccessSnackbar(response.message);
      } else {
        AlertManager.showErrorSnackbar(response.message);
      }
    },
    requestGetBusinessUnitList: async function() {
      const response = await BusinessUnitApi.requestBusinessUnitList();
      if (response.status) {
        this.business_unit_list = response.payload;
      } else {
        AlertManager.showErrorSnackbar(response.message);
      }
    }
  },
  async beforeMount() {
    await this.requestGetBusinessUnitList();
    if (this.isEditMode) {
      this.username = this.selected_user.username;
      this.fullname = this.selected_user.name;
      this.email = this.selected_user.email;
      this.remark = this.selected_user.remark;
      this.selected_role = this.selected_user.role;
      this.mappingBusinessUnitAndManagerById(
        this.selected_user.business_unit,
        this.selected_user.supervisor
      );
    }
  }
};
</script>

<style scoped>
.fixed-width {
  max-width: 480px;
  padding: 0 20px;
}
.column {
  padding: 0.5rem;
}
</style>
